"use client";

import {
  SimpleGrid, Group, Box, Text, Badge, Stack, Tooltip, Transition, SegmentedControl,
} from "@forestsync/ui";
import { useState } from "react";
import { useTranslations } from "next-intl";
import { AggregatedDepthChart } from "./AggregatedDepthChart";
import type { OrderBook } from "@/lib/queries";
import type { TradeFill } from "./TradeBlotter";

interface DepthFeedPanelProps {
  orderBook: OrderBook;
  recentFills: TradeFill[];
}

const PRECISION_OPTIONS = ["0.01", "0.10", "1.00"]

function formatTime(date: Date): string {
  const isToday = new Date().toDateString() === date.toDateString()
  if (isToday) {
    return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
  }
  return date.toLocaleString("en-GB", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
}

export function DepthFeedPanel({ orderBook, recentFills }: DepthFeedPanelProps) {
  const t = useTranslations("trading");
  const [precision, setPrecision] = useState("1.00");

  return (
    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md" p={8}>
      {/* Market depth chart */}
      <Box>
        <Group justify="space-between" mb={8}>
          <Text size="sm" fw={600}>{t("marketDepth")}</Text>
          <SegmentedControl
            size="xs"
            data={PRECISION_OPTIONS}
            value={precision}
            onChange={setPrecision}
          />
        </Group>
        <AggregatedDepthChart
          orderBook={orderBook}
          precision={Number(precision)}
        />
      </Box>

      {/* Activity feed */}
      <Box>
        <Text fw={600} size="sm" mb={8}>{t("activityFeed")}</Text>
        <Stack gap={2} h={300} style={{ overflowY: "auto" }}>
          {recentFills.slice(0, 50).map((fill, index) => (
            <Transition
              key={fill.id}
              mounted={true}
              transition="slide-down"
              duration={index === 0 ? 300 : 0}
            >
              {(styles) => (
                <Group gap={6} style={styles} wrap="nowrap">
                  <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
                    {formatTime(fill.time)}
                  </Text>
                  <Badge
                    size="xs"
                    color={fill.side === 2 ? "green" : "red"}
                    variant="filled"
                  >
                    {fill.side === 2 ? "BUY" : "SELL"}
                  </Badge>
                  <Text size="xs" fw={500}>€{fill.price.toFixed(2)}</Text>
                  <Text size="xs" c="dimmed">{fill.volume.toFixed(2)}</Text>
                </Group>
              )}
            </Transition>
          ))}
        </Stack>
      </Box>
    </SimpleGrid>
  );
}
